/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import { useState } from 'react';
import type { ChangeEvent, FunctionComponent, KeyboardEvent, ReactNode } from 'react';

import { useMediaQuery, useClickOutside } from '@mantine/hooks';
import { breakpoints } from 'styles/theme.variables';

import { Text, TextInput } from '@mantine/core';
import { CloseIconSm } from '@chownow/pantry/icons';
import { KEY_NAMES } from 'constants/global';

/** Returns the message to show under the input, or null when the value is valid. */
type Validator = (value: string) => string | null | undefined;

type EditableTextProps = {
  disableEdit?: boolean;
  /** Callers save asynchronously; the return value is ignored either way. */
  handleEdit: (textKey: string, value: string) => void | Promise<void>;
  isRequired?: boolean;
  placeholder?: string;
  rawText: string;
  subText?: string;
  /** The read-mode rendering of `rawText` - may be a placeholder when `rawText` is empty. */
  text: ReactNode;
  textKey: string;
  validator?: Validator;
};

function getInitialValue(rawText: string): string {
  if (!rawText || rawText === '—') return '';
  return rawText;
}

export const EditableText: FunctionComponent<EditableTextProps> = ({
  disableEdit,
  handleEdit,
  isRequired,
  placeholder,
  rawText,
  subText,
  text,
  textKey,
  validator = () => null,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.lg})`);

  const handleSave = () => {
    if (error) {
      setIsEditing(false);
      setCurrentValue(getInitialValue(rawText));
      setError(null);
      return;
    }
    // whitespace-only is not a value
    const nextValue = currentValue.trim();

    if (!isRequired || nextValue) {
      // compare against the normalized value so an untouched empty field doesn't save
      if (nextValue !== getInitialValue(rawText)) {
        // fire-and-forget: callers own their own error notifications
        void handleEdit(textKey, nextValue);
      }
    }
    setIsEditing(false);
  };

  // need to use click outside in order to allow use of the clear button in the input
  const ref = useClickOutside(handleSave);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === KEY_NAMES.enter) {
      event.preventDefault();
      handleSave();
    }
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(event.currentTarget.value);
    setError(validator(event.currentTarget.value) ?? null);
  };

  const handleClear = () => {
    if (!isRequired) {
      setCurrentValue('');
    }
  };

  const startEditing = () => {
    if (disableEdit) return;

    if (isDesktop) {
      setCurrentValue(getInitialValue(rawText));
      setIsEditing(true);
    }
  };

  if (isEditing) {
    return (
      <div ref={ref} style={{ width: '100%' }}>
        <TextInput
          value={currentValue}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          rightSectionPointerEvents="all"
          rightSection={
            <CloseIconSm
              aria-label="Clear input"
              onClick={handleClear}
              style={{ display: currentValue && !isRequired ? undefined : 'none' }}
            />
          }
          size="sm"
          autoFocus
          fw="var(--font-weight-regular)"
          placeholder={placeholder}
          error={error}
        />
      </div>
    );
  }

  return (
    <>
      <div
        onClick={startEditing}
        style={{
          cursor: disableEdit ? 'not-allowed' : 'text',
        }}
      >
        {text}
      </div>
      {subText && (
        <Text variant="sublabel-xs" c="var(--content-secondary)" mt="xs3">
          {subText}
        </Text>
      )}
    </>
  );
};
